import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Minus, HelpCircle } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { cn } from '../lib/utils';
type FAQItem = {
  question: string;
  answer: React.ReactNode;
};
const faqs: FAQItem[] = [{
  question: 'What is DTF (Direct to Film) printing?',
  answer: 'DTF (Direct to Film) is a printing technology where designs are printed onto a special film using water-based inks, then coated with a powder adhesive and cured. The resulting transfer can be heat pressed onto almost any fabric (cotton, polyester, blends, leather, etc.) with vibrant colors and high durability.'
}, {
  question: 'What are the artwork requirements for DTF transfers?',
  answer: <ul className="list-disc pl-5 space-y-2" data-id="element-1092">
        <li data-id="element-1093">
          File types: PNG, AI, PDF, or PSD (Transparent background required for
          PNG)
        </li>
        <li data-id="element-1094">Resolution: 300 DPI is recommended for best quality</li>
        <li data-id="element-1095">Color Mode: CMYK is preferred for color accuracy</li>
        <li data-id="element-1096">Do not mirror your artwork; we handle that during printing</li>
      </ul>
}, {
  question: 'How durable are DTF transfers?',
  answer: 'DTF transfers are extremely durable. When pressed correctly, they can last 50+ washes without cracking or fading. They are stretchy and soft to the touch, making them superior to traditional vinyl or screen print transfers for many applications.'
}, {
  question: 'What is a Gang Sheet?',
  answer: 'A Gang Sheet is a large sheet of transfer film (e.g., 22" x 60") where you can place multiple designs of different sizes. This allows you to maximize the space and get the best price per print. You can upload a pre-built gang sheet or use our online builder to arrange your designs.'
}, {
  question: 'What is your standard turnaround time?',
  answer: 'For most DTF transfer orders, we ship within 24-48 hours. Custom apparel orders typically take 5-7 business days depending on the quantity and complexity. Rush options are available at checkout for urgent deadlines.'
}, {
  question: 'Do you have order minimums?',
  answer: 'No! We have no minimums for DTF transfers. You can order a single sheet or thousands. For custom apparel (screen printing or embroidery), minimums may apply depending on the decoration method.'
}, {
  question: 'What temperature and pressure should I use for pressing?',
  answer: <div className="space-y-2" data-id="element-1097">
        <p data-id="element-1098">General guidelines for DTF transfers:</p>
        <ul className="list-disc pl-5 space-y-1" data-id="element-1099">
          <li data-id="element-1100">Temperature: 300°F - 320°F (150°C - 160°C)</li>
          <li data-id="element-1101">Time: 10-15 seconds</li>
          <li data-id="element-1102">Pressure: Medium to High (60PSI)</li>
          <li data-id="element-1103">Peel: Cold peel (wait until the transfer is completely cool)</li>
          <li data-id="element-1104">
            Second Press: Cover with parchment paper or Teflon and press for
            another 5-10 seconds for durability.
          </li>
        </ul>
      </div>
}, {
  question: 'Can I apply DTF transfers to any fabric?',
  answer: 'Yes, DTF is incredibly versatile. It works great on 100% cotton, 50/50 blends, polyester, nylon, leather, denim, and even canvas. Unlike sublimation, it works on dark and light colored fabrics.'
}, {
  question: 'Do you offer design services?',
  answer: 'Yes, we have an in-house design team that can help you with vectorizing logos, creating custom designs, or fixing low-quality artwork. Contact us for a quote on design services.'
}, {
  question: 'What is your return policy?',
  answer: 'Since our products are custom-made, we cannot accept returns for user errors (such as low-quality artwork or incorrect sizing). However, if there is a defect in the print quality or an error on our end, please contact us within 7 days of receiving your order, and we will reprint or refund your order.'
}];
const AccordionItem = ({
  item,
  isOpen,
  onClick
}: {
  item: FAQItem;
  isOpen: boolean;
  onClick: () => void;
}) => {
  return <div className="border-b border-white/10 last:border-0" data-id="element-1105">
      <button onClick={onClick} className="w-full py-6 flex items-center justify-between text-left group transition-colors" data-id="element-1106">
        <span className={cn('text-lg font-medium transition-colors pr-8', isOpen ? 'text-atomic-green' : 'text-white group-hover:text-gray-300')} data-id="element-1107">
          {item.question}
        </span>
        <span className={cn('flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center border transition-all duration-300', isOpen ? 'border-atomic-green bg-atomic-green/10 text-atomic-green rotate-180' : 'border-white/20 text-gray-400 group-hover:border-white/40 group-hover:text-white')} data-id="element-1108">
          {isOpen ? <Minus size={16} data-id="element-1109" /> : <Plus size={16} data-id="element-1110" />}
        </span>
      </button>
      <AnimatePresence data-id="element-1111">
        {isOpen && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.3,
        ease: 'easeInOut'
      }} className="overflow-hidden" data-id="element-1112">
            <div className="pb-6 text-gray-400 leading-relaxed" data-id="element-1113">
              {item.answer}
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
};
export function FAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  return <main className="min-h-screen bg-atomic-black pt-24 pb-20" data-id="element-1114">
      {/* Hero Section */}
      <section className="container mx-auto px-4 md:px-6 mb-16" data-id="element-1115">
        <div className="max-w-3xl mx-auto text-center" data-id="element-1116">
          <div className="inline-flex items-center justify-center p-3 bg-white/5 rounded-full mb-6" data-id="element-1117">
            <HelpCircle className="w-6 h-6 text-atomic-green" data-id="element-1118" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6" data-id="element-1119">
            Frequently Asked{' '}
            <span className="text-atomic-green" data-id="element-1120">Questions</span>
          </h1>
          <p className="text-xl text-gray-400" data-id="element-1121">
            Everything you need to know about our DTF printing services,
            shipping, and custom apparel.
          </p>
        </div>
      </section>

      {/* FAQ Accordion */}
      <section className="container mx-auto px-4 md:px-6" data-id="element-1122">
        <div className="max-w-3xl mx-auto bg-atomic-charcoal rounded-2xl border border-white/10 p-6 md:p-10 shadow-2xl" data-id="element-1123">
          {faqs.map((faq, index) => <AccordionItem key={index} item={faq} isOpen={openIndex === index} onClick={() => setOpenIndex(openIndex === index ? null : index)} data-id="element-1124" />)}
        </div>
      </section>

      {/* Still have questions CTA */}
      <section className="container mx-auto px-4 md:px-6 mt-20" data-id="element-1125">
        <div className="max-w-4xl mx-auto bg-gradient-to-r from-atomic-charcoal to-atomic-black border border-white/10 rounded-2xl p-8 md:p-12 text-center relative overflow-hidden" data-id="element-1126">
          <div className="absolute top-0 right-0 w-64 h-64 bg-atomic-green/5 rounded-full blur-3xl -mr-32 -mt-32 pointer-events-none" data-id="element-1127" />
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-blue-500/5 rounded-full blur-3xl -ml-32 -mb-32 pointer-events-none" data-id="element-1128" />

          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4 relative z-10" data-id="element-1129">
            Still have questions?
          </h2>
          <p className="text-gray-400 mb-8 max-w-xl mx-auto relative z-10" data-id="element-1130">
            Can't find the answer you're looking for? Our team is here to help
            you with any custom requests or technical questions.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center relative z-10" data-id="element-1131">
            <a href="/contact" data-id="element-1132">
              <Button size="lg" data-id="element-1133">Contact Support</Button>
            </a>
            <a href="tel:517-414-9232" data-id="element-1134">
              <Button variant="outline" size="lg" className="bg-transparent border-white/20 text-white hover:bg-white/5" data-id="element-1135">
                Call 517-414-9232
              </Button>
            </a>
          </div>
        </div>
      </section>
    </main>;
}